import React, { useState, useCallback, useMemo, useEffect, Suspense, memo, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import styled from 'styled-components';
import { mainApi } from "./api/mainApi";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./css/layout.css";

// 개발 환경에서만 성능 모니터링
const isDev = process.env.NODE_ENV === 'development';

// 성능 측정을 위한 래퍼 컴포넌트 - 개선된 버전
const withPerformanceMonitor = (Component, name) => {
  if (!isDev) return Component;
  
  const PerformanceMonitor = props => {
    const renderCount = useRef(0);
    const startTime = useRef(performance.now());
    
    useEffect(() => {
      renderCount.current += 1;
      const renderTime = performance.now() - startTime.current;
      console.log(
        `[Performance] ${name} rendered ${renderCount.current} times | ${renderTime.toFixed(2)}ms`
      );
      
      startTime.current = performance.now();
    });
    
    return <Component {...props} />;
  };
  
  PerformanceMonitor.displayName = `PerformanceMonitor(${name})`;
  return PerformanceMonitor;
};

// 최적화된 debounce 함수 구현
const debounce = (func, wait) => {
  let timeout;
  
  return function executedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      func.apply(context, args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 컴포넌트 사전 로딩 - 코드 스플리팅 최적화
const HeaderContainer = React.lazy(() => import(/* webpackChunkName: "header" */ "./header/HeaderContainer"));
const MiddleSection = React.lazy(() => import(/* webpackChunkName: "middle" */ "./body/MiddleHome"));
const MainContainer = React.lazy(() => import(/* webpackChunkName: "main-container" */ './body/mainPage/MainPageContainer'));
const MailAlertContainer = React.lazy(() => import(/* webpackChunkName: "mail-alert" */ './body/mailAlert/MailAlertContainer'));
const UserContainer = React.lazy(() => import(/* webpackChunkName: "user" */ './body/mainUser/UserContainer'));
const MailHistoryContainer = React.lazy(() => import(/* webpackChunkName: "mail-history" */ './body/mailHistory/MailHistoryContainer'));
const MailCheck = React.lazy(() => import(/* webpackChunkName: "mail-check" */ './body/mailAlert/MailCheck'));
const MailDetailContainer = React.lazy(() => import(/* webpackChunkName: "mail-detail" */ './body/mailHistoryDetail/MailDetailContainer'));
const LoginContainer = React.lazy(() => import(/* webpackChunkName: "login" */ './body/login/LoginContainer'));
const SettingsContainer = React.lazy(() => import(/* webpackChunkName: "settings" */ './body/settings/SettingsContainer'));

// 스타일드 컴포넌트 정의 - 간결화
const AppWrapper = styled.div`
  height: 100vh;
  background-color: #fff;
`;

// 로딩 컴포넌트 - 최적화
const LoadingFallback = memo(() => (
  <div className="loading-indicator">로딩 중...</div>
));
LoadingFallback.displayName = 'LoadingFallback';

// 컨텐츠 래퍼 컴포넌트 - 경로 패턴 최적화
const ContentWrapper = memo(({ children }) => {
  const location = useLocation();
  const containerClass = useMemo(() => {
    const path = location.pathname;
    
    // 패턴 기반 조건 검사로 개선
    if (path.startsWith('/alerts/')) {
      if (path === '/alerts/send' || path === '/alerts/check') {
        return 'alert-container';
      }
    } else if (path === '/userList') {
      return 'user-container';
    }
    
    return 'content-container';
  }, [location.pathname]);

  return <div className={containerClass}>{children}</div>;
});
ContentWrapper.displayName = 'ContentWrapper';

// Suspense 래퍼 컴포넌트 - 지연 로딩 관리
const LazyComponent = memo(({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
));
LazyComponent.displayName = 'LazyComponent';

// 메인 앱 컴포넌트 - 최적화
const MainAppBase = () => {
  // 상태 관리 - 초기값 최적화
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [userList, setUserList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // 인증
  const { user, loading: authLoading } = useAuth();

  // 라우팅
  const location = useLocation();
  const isMainPage = useMemo(() =>
    location.pathname === '/main',
    [location.pathname]
  );

  // API 호출 디바운싱 - 최적화
  const fetchUserData = useCallback(async (year, month) => {
    try {
      const response = await mainApi.fetchUserList(year, month);
      if (!response) return;
      
      const allUsersList = Object.values(response).flat();
      setUserList(prev => {
        // 이전 상태와 비교하여 변경이 있을 때만 업데이트
        if (JSON.stringify(prev) !== JSON.stringify(allUsersList)) {
          return allUsersList;
        }
        return prev;
      });
    } catch (error) {
      console.error("데이터 조회 실패:", error);
      setUserList([]);
    }
  }, []);

  const debouncedFetchUserData = useMemo(() => 
    debounce(fetchUserData, 150), 
    [fetchUserData]
  );

  // 데이터 로딩 효과 - 의존성 최적화
  useEffect(() => {
    if (isMainPage && user) {
      debouncedFetchUserData(selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth, debouncedFetchUserData, isMainPage, user]);

  // 캐시된 핸들러 함수들 - 최적화
  const handleSelectedUsersChange = useCallback(users => {
    setSelectedUsers(users);
  }, []);

  const handleYearChange = useCallback(year => {
    if (year !== selectedYear) {
      setSelectedYear(year);
    }
  }, [selectedYear]);

  const handleMonthChange = useCallback(month => {
    if (month !== selectedMonth) {
      setSelectedMonth(month);
    }
  }, [selectedMonth]);

  // 캐시된 라우트 구성 - 코드 분할
  const routes = useMemo(() => (
    <Routes>
      <Route path="/" element={<Navigate to="/main" replace />} />
      <Route path="/login" element={<Navigate to="/main" replace />} />
      <Route
        path="/main"
        element={
          <MainContainer
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            setSelectedYear={handleYearChange}
            setSelectedMonth={handleMonthChange}
            userList={userList}
            selectedUsers={selectedUsers}
            onSelectedUsersChange={handleSelectedUsersChange}
            onDataRefresh={() => fetchUserData(selectedYear, selectedMonth)}
          />
        }
      />
      <Route path="/alerts/send" element={<MailAlertContainer initialData={selectedUsers} />} />
      <Route path="/alerts/check" element={<MailCheck selectedUsers={selectedUsers} />} />
      <Route path="/alerts/history" element={<MailHistoryContainer />} />
      <Route path="/alerts/history/:id" element={<MailDetailContainer />} />
      <Route path="/userList" element={<UserContainer />} />
      <Route path="/settings" element={<SettingsContainer />} />
    </Routes>
  ), [
    selectedYear,
    selectedMonth,
    userList,
    selectedUsers,
    handleYearChange,
    handleMonthChange,
    handleSelectedUsersChange
  ]);

  // 메인 컨텐츠 - 컴포넌트 분리
  const MainContent = useMemo(() => (
    <div className="flex-column">
      <LazyComponent>
        <MiddleSection
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          userList={userList}
        />
      </LazyComponent>
      <ContentWrapper>
        <LazyComponent>
          {routes}
        </LazyComponent>
      </ContentWrapper>
    </div>
  ), [routes, selectedYear, selectedMonth, userList]);

  // 인증 가드 — 세션 확인 중에는 로딩, 미로그인 시 어떤 경로든 로그인 화면
  if (authLoading) {
    return <LoadingFallback />;
  }
  if (!user) {
    return (
      <LazyComponent>
        <LoginContainer />
      </LazyComponent>
    );
  }

  return (
    <AppWrapper className="app-wrapper">
      <LazyComponent>
        <HeaderContainer />
      </LazyComponent>
      {MainContent}
    </AppWrapper>
  );
};

// 성능 모니터링 추가 - 개선된 버전
const MainApp = withPerformanceMonitor(MainAppBase, 'MainApp');

// 앱 라우터 컴포넌트 - 최적화
const App = memo(() => (
  <BrowserRouter future={{
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }}>
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  </BrowserRouter>
));
App.displayName = 'App';

export default App;